import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Calculator,
  Euro,
  TrendingUp,
  Calendar,
  Info,
  CheckCircle2,
  BarChart3,
  FileText,
  Loader2,
  Layers,
  ShieldCheck,
  Home,
} from "lucide-react";
import { getApiUrl } from "@/config";

// Tassi ammessi dalla tabella Creditplan: 0.00% – 6.90% con step 0.10
const TASSI = Array.from({ length: 70 }, (_, i) => (i * 0.1).toFixed(2));
// Durate ammesse dalla tabella Creditplan
const DURATE = [5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30, 35, 40];

function formatEuro(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value) {
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function StatCard({ icon: Icon, label, value, sublabel, color = "slate", badge }) {
  const iconColorMap = {
    slate: "text-gray-600 bg-gray-100",
    blue: "text-blue-600 bg-blue-100/60",
    emerald: "text-emerald-600 bg-emerald-100/60",
    amber: "text-amber-600 bg-amber-100/60",
    violet: "text-violet-600 bg-violet-100/60",
    rose: "text-rose-600 bg-rose-100/60",
    teal: "text-teal-600 bg-teal-100/60",
  };

  return (
    <div className="bg-white/70 rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${iconColorMap[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {badge && (
          <Badge variant="outline" className="text-xs font-medium">
            {badge}
          </Badge>
        )}
      </div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sublabel && (
        <p className="text-xs text-gray-500 mt-1">{sublabel}</p>
      )}
    </div>
  );
}

export default function PreventivatoreMutuo() {
  const [importo, setImporto] = useState("200000");
  const [tasso, setTasso] = useState("");
  const [durata, setDurata] = useState("");
  const [risultato, setRisultato] = useState(null);
  const [errori, setErrori] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const importoNum = parseFloat(importo) || 0;

  const valida = () => {
    const e = {};
    if (!importo || importoNum <= 0) e.importo = "Inserisci un importo valido";
    if (!tasso) e.tasso = "Seleziona un tasso di interesse";
    if (!durata) e.durata = "Seleziona la durata";
    setErrori(e);
    return Object.keys(e).length === 0;
  };

  const calcola = async () => {
    if (!valida()) return;
    setLoading(true);
    setApiError(null);
    try {
      const response = await fetch(getApiUrl("/api/preventivatore-mutuo"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          capitaleA: importoNum,
          tasso: parseFloat(tasso),
          anni: parseInt(durata),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Errore nella risposta del server");
      }

      const { breakdown } = data;
      const rataA = parseFloat(breakdown.rataA);
      const rataB = parseFloat(breakdown.rataB);
      const rata = parseFloat(breakdown.rataMensileTotale);
      const totaleErogatoServizi = parseFloat(breakdown.totaleErogatoServizi);
      const impostaSostitutiva = parseFloat(breakdown.impostaSostitutiva);
      const piano = breakdown.piano;
      const n = piano.length;

      setRisultato({
        rata,
        rataA,
        rataB,
        totaleErogatoServizi,
        impostaSostitutiva,
        totalePagato: rata * n,
        totaleInteressiA: piano.reduce((acc, r) => acc + parseFloat(r.quotaInt), 0),
        piano,
        capitale: importoNum,
        tasso: parseFloat(tasso),
        anni: parseInt(durata),
      });
    } catch (err) {
      console.error("Errore preventivatore mutuo:", err);
      setApiError(err.message || "Impossibile connettersi al server. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const pianoAnnuale = useMemo(() => {
    if (!risultato) return [];
    const map = {};
    risultato.piano.forEach((r) => {
      const anno = Math.ceil(r.numeroRata / 12);
      if (!map[anno])
        map[anno] = { anno, rate: 0, capitale: 0, interessi: 0, saldoFinale: 0 };
      map[anno].rate += parseFloat(r.rataA);
      map[anno].capitale += parseFloat(r.quotaCap);
      map[anno].interessi += parseFloat(r.quotaInt);
      map[anno].saldoFinale = parseFloat(r.debitoResiduo);
    });
    return Object.values(map);
  }, [risultato]);

  const percInteressi = risultato
    ? (risultato.totaleInteressiA / (risultato.capitale + risultato.totaleInteressiA)) * 100
    : 0;

  return (
    <TooltipProvider>
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-teal-50/30 via-emerald-50/20 to-cyan-50/30 p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto space-y-6">

          {/* Header */}
          <div className="bg-teal-100/40 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <Home className="h-7 w-7 text-teal-600" />
                  Preventivatore Mutuo
                </h1>
                <p className="text-gray-600">Calcola la rata mensile e il piano di ammortamento del mutuo</p>
              </div>
              <div className="hidden md:flex items-center gap-3">
                <div className="bg-white/60 rounded-xl px-4 py-2 shadow-sm">
                  <p className="text-xs text-gray-500 mb-1">Valuta</p>
                  <p className="text-sm font-semibold text-gray-900">EUR €</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Form Card */}
            <div className="col-span-12 lg:col-span-4">
              <Card className="sticky top-6 bg-white/60 border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-teal-500" />
                    Parametri del Mutuo
                  </CardTitle>
                  <CardDescription>
                    Inserisci i dati per calcolare il preventivo
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Importo */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="importo" className="text-sm font-medium">
                        Importo del Mutuo
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Capitale A — importo netto finanziato in €</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="importo"
                        type="number"
                        min="1"
                        step="10000"
                        value={importo}
                        onChange={(e) => {
                          setImporto(e.target.value);
                          setErrori((prev) => ({ ...prev, importo: undefined }));
                        }}
                        className={`pl-9 rounded-xl bg-white/80 shadow-sm ${errori.importo ? "border-red-400" : ""}`}
                        placeholder="200000"
                      />
                    </div>
                    {errori.importo && (
                      <p className="text-xs text-red-500">{errori.importo}</p>
                    )}
                    {importoNum > 0 && (
                      <p className="text-xs text-slate-500">{formatEuro(importoNum)}</p>
                    )}
                  </div>

                  <Separator />

                  {/* Tasso */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Tasso di Interesse Annuo</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Tasso Annuo Nominale (TAN) — range 0.00% – 6.90%</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select
                      value={tasso}
                      onValueChange={(v) => {
                        setTasso(v);
                        setErrori((prev) => ({ ...prev, tasso: undefined }));
                      }}
                    >
                      <SelectTrigger className={`rounded-xl ${errori.tasso ? "border-red-400" : ""}`}>
                        <SelectValue placeholder="Seleziona Tasso" />
                      </SelectTrigger>
                      <SelectContent>
                        {TASSI.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errori.tasso && (
                      <p className="text-xs text-red-500">{errori.tasso}</p>
                    )}
                  </div>

                  {/* Durata */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Durata del Finanziamento</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Numero di anni del piano di rimborso: 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30, 35 o 40</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select
                      value={durata}
                      onValueChange={(v) => {
                        setDurata(v);
                        setErrori((prev) => ({ ...prev, durata: undefined }));
                      }}
                    >
                      <SelectTrigger className={`rounded-xl ${errori.durata ? "border-red-400" : ""}`}>
                        <SelectValue placeholder="Seleziona Anni" />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATE.map((a) => (
                          <SelectItem key={a} value={String(a)}>
                            {a} anni
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errori.durata && (
                      <p className="text-xs text-red-500">{errori.durata}</p>
                    )}
                  </div>

                  <Separator />

                  {apiError && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertTitle className="text-red-800 text-sm font-semibold">Errore</AlertTitle>
                      <AlertDescription className="text-red-700 text-xs">{apiError}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold h-11 text-sm tracking-wide rounded-xl"
                    onClick={calcola}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Calcolo in corso...
                      </>
                    ) : (
                      <>
                        <Calculator className="h-4 w-4 mr-2" />
                        CALCOLA PREVENTIVO
                      </>
                    )}
                  </Button>

                  {risultato && (
                    <Alert className="border-emerald-200 bg-emerald-50">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <AlertTitle className="text-emerald-800 text-sm font-semibold">
                        Preventivo calcolato
                      </AlertTitle>
                      <AlertDescription className="text-emerald-700 text-xs">
                        Piano con {risultato.anni * 12} rate mensili · TAN {risultato.tasso}%
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Results */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              {!risultato ? (
                <Card className="border-dashed bg-white/40 shadow-sm rounded-2xl">
                  <CardContent className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="p-4 bg-teal-50 rounded-2xl mb-4">
                      <BarChart3 className="h-10 w-10 text-teal-400" />
                    </div>
                    <p className="text-slate-600 font-medium text-lg mb-1">
                      Nessun preventivo calcolato
                    </p>
                    <p className="text-slate-400 text-sm max-w-xs">
                      Inserisci i parametri del mutuo e premi{" "}
                      <span className="font-semibold text-slate-600">Calcola Preventivo</span>{" "}
                      per visualizzare il piano di rimborso
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard
                      icon={Euro}
                      label="Rata Mensile Totale"
                      value={formatEuro(risultato.rata)}
                      sublabel={`A: ${formatEuro(risultato.rataA)} + B: ${formatEuro(risultato.rataB)}`}
                      color="teal"
                      badge="TOTALE"
                    />
                    <StatCard
                      icon={Layers}
                      label="Servizi Finanziari"
                      value={formatEuro(risultato.totaleErogatoServizi)}
                      sublabel="Capitale B (incl. polizze)"
                      color="violet"
                    />
                    <StatCard
                      icon={TrendingUp}
                      label="Totale Pagato"
                      value={formatEuro(risultato.totalePagato)}
                      sublabel={`${risultato.anni * 12} rate`}
                      color="amber"
                    />
                    <StatCard
                      icon={Calendar}
                      label="Durata"
                      value={`${risultato.anni} anni`}
                      sublabel={`TAN ${risultato.tasso}%`}
                      color="emerald"
                    />
                  </div>

                  {/* Dettaglio Rata */}
                  <Card className="bg-white/60 border-0 shadow-sm rounded-2xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-teal-500" />
                        Composizione della Rata Mensile
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Dettaglio Rata A (capitale) + Rata B (servizi) + Spese Incasso
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-teal-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Rata A</p>
                          <p className="text-xl font-bold text-teal-700">{formatEuro(risultato.rataA)}</p>
                          <p className="text-xs text-gray-500 mt-1">Rimborso capitale</p>
                        </div>
                        <div className="bg-violet-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Rata B</p>
                          <p className="text-xl font-bold text-violet-700">{formatEuro(risultato.rataB)}</p>
                          <p className="text-xs text-gray-500 mt-1">Servizi finanziari</p>
                        </div>
                        <div className="bg-slate-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Spese Incasso</p>
                          <p className="text-xl font-bold text-slate-700">{formatEuro(2.00)}</p>
                          <p className="text-xs text-gray-500 mt-1">Per rata</p>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs text-gray-600 font-medium">
                            <span className="flex items-center gap-1.5">
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-teal-500" />
                              Rata A — Capitale ({formatNumber((risultato.rataA / risultato.rata) * 100)}%)
                            </span>
                            <span>{formatEuro(risultato.rataA)}</span>
                          </div>
                          <Progress
                            value={(risultato.rataA / risultato.rata) * 100}
                            className="h-2 bg-violet-100 [&>div]:bg-teal-500"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs text-gray-600 font-medium">
                            <span className="flex items-center gap-1.5">
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-violet-500" />
                              Rata B — Servizi ({formatNumber((risultato.rataB / risultato.rata) * 100)}%)
                            </span>
                            <span>{formatEuro(risultato.rataB)}</span>
                          </div>
                          <Progress
                            value={(risultato.rataB / risultato.rata) * 100}
                            className="h-2 bg-teal-100 [&>div]:bg-violet-500"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2 border">
                          <span className="text-xs text-gray-500 font-medium">Capitale A:</span>
                          <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">
                            {formatEuro(risultato.capitale)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2 border">
                          <span className="text-xs text-gray-500 font-medium">Servizi (B):</span>
                          <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">
                            {formatEuro(risultato.totaleErogatoServizi)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2 border">
                          <span className="text-xs text-gray-500 font-medium">TAN:</span>
                          <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">
                            {risultato.tasso}%
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 italic pt-1">* Escluse imposte e tasse</p>
                    </CardContent>
                  </Card>

                  {/* Interessi totali */}
                  <Card className="bg-white/60 border-0 shadow-sm rounded-2xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-amber-500" />
                        Riepilogo Costo del Mutuo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-teal-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Capitale Finanziato</p>
                          <p className="text-xl font-bold text-teal-700">{formatEuro(risultato.capitale)}</p>
                        </div>
                        <div className="bg-amber-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Totale Interessi (A)</p>
                          <p className="text-xl font-bold text-amber-700">{formatEuro(risultato.totaleInteressiA)}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatNumber(percInteressi)}% del capitale</p>
                        </div>
                        <div className="bg-slate-50/60 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Totale Rate Pagate</p>
                          <p className="text-xl font-bold text-slate-700">{formatEuro(risultato.totalePagato)}</p>
                          <p className="text-xs text-gray-500 mt-1">{risultato.anni * 12} rate mensili</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Piano di Ammortamento */}
                  <Card className="bg-white/60 border-0 shadow-sm rounded-2xl">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-sm font-semibold text-gray-700">
                            Piano di Ammortamento — Capitale A
                          </CardTitle>
                          <CardDescription className="text-xs mt-1">
                            Quota capitale e interessi su {formatEuro(risultato.capitale)} · mutuo
                          </CardDescription>
                        </div>
                        <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 border border-teal-200">
                          {risultato.anni * 12} rate
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Tabs defaultValue="annuale">
                        <div className="px-6 pb-3">
                          <TabsList className="w-full sm:w-auto">
                            <TabsTrigger value="annuale" className="text-xs">
                              Vista Annuale
                            </TabsTrigger>
                            <TabsTrigger value="mensile" className="text-xs">
                              Vista Mensile
                            </TabsTrigger>
                          </TabsList>
                        </div>

                        <TabsContent value="annuale" className="mt-0">
                          <ScrollArea className="h-[380px]">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                  <TableHead className="text-xs font-semibold text-slate-600 w-20 pl-6">Anno</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Tot. Rate A</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Quota Cap.</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Quota Int.</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right pr-6">Debito Residuo</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pianoAnnuale.map((a) => (
                                  <TableRow
                                    key={a.anno}
                                    className={a.anno % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-50"}
                                  >
                                    <TableCell className="text-xs font-semibold text-slate-700 pl-6">Anno {a.anno}</TableCell>
                                    <TableCell className="text-xs text-right font-semibold text-slate-800">{formatEuro(a.rate)}</TableCell>
                                    <TableCell className="text-xs text-right text-slate-700">{formatEuro(a.capitale)}</TableCell>
                                    <TableCell className="text-xs text-right text-amber-600 font-medium">{formatEuro(a.interessi)}</TableCell>
                                    <TableCell className="text-xs text-right text-slate-600 pr-6">{formatEuro(a.saldoFinale)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </ScrollArea>
                        </TabsContent>

                        <TabsContent value="mensile" className="mt-0">
                          <ScrollArea className="h-[380px]">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                  <TableHead className="text-xs font-semibold text-slate-600 w-16 pl-6">N°</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Rata A</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Quota Cap.</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right">Quota Int.</TableHead>
                                  <TableHead className="text-xs font-semibold text-slate-600 text-right pr-6">Debito Residuo</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {risultato.piano.map((r) => (
                                  <TableRow
                                    key={r.numeroRata}
                                    className={r.numeroRata % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-50"}
                                  >
                                    <TableCell className="text-xs font-medium text-slate-500 pl-6">{r.numeroRata}</TableCell>
                                    <TableCell className="text-xs text-right font-semibold text-slate-800">{formatEuro(parseFloat(r.rataA))}</TableCell>
                                    <TableCell className="text-xs text-right text-slate-700">{formatEuro(parseFloat(r.quotaCap))}</TableCell>
                                    <TableCell className="text-xs text-right text-amber-600 font-medium">{formatEuro(parseFloat(r.quotaInt))}</TableCell>
                                    <TableCell className="text-xs text-right text-slate-600 pr-6">{formatEuro(parseFloat(r.debitoResiduo))}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </ScrollArea>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
